import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useClient } from "@/context/ClientContext";
import {
  mediaList, mediaUpload, mediaPatch, mediaDelete, mediaUsage,
  mediaEnhance, mediaCaption, mediaPrefs, mediaSetPrefs,
} from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, Star, Archive, Trash, Sparkle, Images, Warning, HardDrives } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SOURCE_STYLE = {
  client_uploaded: "bg-emerald-500/15 text-emerald-400", ai_generated: "bg-amber-500/15 text-amber-300",
  product: "bg-sky-500/15 text-sky-300", branded_graphic: "bg-[#FF5E00]/15 text-[#FF5E00]",
  stock: "bg-white/10 text-[#9a9a9a]", imported: "bg-violet-500/15 text-violet-300",
};
const ENHANCE_ACTIONS = [
  ["improve_lighting", "Improve lighting"], ["sharpen", "Sharpen"], ["straighten", "Straighten"],
  ["crop_platform", "Crop for platform"], ["branded_border", "Branded border"],
  ["add_logo", "Add my logo"], ["remove_background", "Remove background"],
  ["blur_region", "Blur area (plates/faces/addresses)"], ["before_after", "Before / After graphic"],
];

function UploadZone({ cid, onDone }) {
  const [rights, setRights] = useState(false);
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef();
  const catsQ = useQuery({ queryKey: ["media", cid, "cats"], queryFn: () => mediaList(cid), enabled: !!cid });

  const doUpload = async (files) => {
    if (!files.length) return;
    if (!rights) return toast.error("Please confirm you own these photos or have permission to use them.");
    setBusy(true);
    const form = new FormData();
    form.append("client_id", cid);
    form.append("rights_confirmed", "true");
    if (category) form.append("category", category);
    Array.from(files).slice(0, 20).forEach((f) => form.append("files", f));
    try {
      const r = await mediaUpload(form);
      r.results.forEach((x) => {
        if (x.ok) {
          (x.warnings || []).forEach((w) => toast.warning(`${x.filename}: ${w}`));
        } else toast.error(`${x.filename}: ${x.error}`);
      });
      const ok = r.results.filter((x) => x.ok).length;
      if (ok) toast.success(`${ok} photo${ok > 1 ? "s" : ""} added to your Media Library`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="media-upload-zone"
      className={`rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${drag ? "border-[#FF5E00] bg-[#FF5E00]/5" : "border-white/15 bg-white/[0.02]"}`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); doUpload(e.dataTransfer.files); }}>
      <UploadSimple size={28} className="mx-auto text-[#FF5E00] mb-2" />
      <div className="font-semibold">Drag & drop your real business photos here</div>
      <div className="text-xs text-[#9a9a9a] mt-1">JPG, PNG, WEBP or HEIC · up to 20 at once · works from mobile camera too</div>
      <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="h-9 w-52 bg-white/5 border-white/10" data-testid="upload-category-select"><SelectValue placeholder="Category (optional)" /></SelectTrigger>
          <SelectContent className="bg-[#16161c] border-white/10 text-white">
            {(catsQ.data?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button disabled={busy} onClick={() => fileRef.current?.click()} data-testid="media-upload-btn"
          className="bg-[#FF5E00] hover:bg-[#ff7a2b] text-black font-semibold">
          {busy ? "Uploading…" : "Choose photos"}
        </Button>
        <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic" capture="environment"
          hidden onChange={(e) => { doUpload(e.target.files); e.target.value = ""; }} />
      </div>
      <label className="flex items-center justify-center gap-2 mt-4 text-xs text-[#c9c9c9] cursor-pointer">
        <input type="checkbox" checked={rights} onChange={(e) => setRights(e.target.checked)}
          className="accent-[#FF5E00]" data-testid="rights-checkbox" />
        I confirm that I own these photos or have permission to use them for marketing.
      </label>
    </div>
  );
}

function EditDialog({ asset, cid, onClose }) {
  const qc = useQueryClient();
  const [m, setM] = useState(asset);
  const [busy, setBusy] = useState("");
  const [blurParams] = useState({ x: 0.3, y: 0.6, w: 0.4, h: 0.25 });
  const usageQ = useQuery({ queryKey: ["media-usage", asset.id], queryFn: () => mediaUsage(asset.id) });
  const inv = () => qc.invalidateQueries({ queryKey: ["media", cid] });

  const save = async () => {
    setBusy("save");
    try {
      await mediaPatch(asset.id, {
        title: m.title, description: m.description, alt_text: m.alt_text,
        tags: typeof m.tags === "string" ? m.tags.split(",").map((t) => t.trim()).filter(Boolean) : m.tags,
        category: m.category, project: m.project, location: m.location, customer_ref: m.customer_ref,
      });
      toast.success("Saved"); inv(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(""); }
  };

  const enhance = async (action) => {
    setBusy(action);
    try {
      const params = action === "blur_region" ? blurParams : action === "crop_platform" ? { aspect: "square" } : {};
      const r = await mediaEnhance(asset.id, action, params);
      toast.success(`New version created: ${r.asset.title}`); inv(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Enhancement failed"); }
    finally { setBusy(""); }
  };

  const genCaption = async () => {
    setBusy("caption");
    try {
      const r = await mediaCaption(asset.id, "Instagram");
      await navigator.clipboard?.writeText(`${r.caption}\n\n${r.hashtags}`).catch(() => {});
      toast.success("AI caption copied to clipboard (grounded in this photo)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Caption failed"); }
    finally { setBusy(""); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-[#111116] border-white/10 text-white max-h-[88vh] overflow-y-auto" data-testid="media-edit-dialog">
        <DialogTitle className="text-white truncate">{asset.title}</DialogTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <img src={`${BACKEND}${asset.path}`} alt={asset.alt_text || asset.title} className="w-full rounded-lg border border-white/10" />
            <div className="flex flex-wrap gap-1.5 mt-3">
              {ENHANCE_ACTIONS.map(([a, l]) => (
                <Button key={a} size="sm" variant="outline" className="border-white/15 h-7 text-[11px]" disabled={!!busy}
                  data-testid={`enhance-${a}`} onClick={() => a === "before_after" ? toast.message("Open the AFTER photo and use Before/After there — or select this as the pair.") : enhance(a)}>
                  {busy === a ? "Working…" : l}
                </Button>
              ))}
              <Button size="sm" className="h-7 text-[11px] bg-[#FF5E00] hover:bg-[#ff7a2b] text-black font-semibold"
                disabled={!!busy} onClick={genCaption} data-testid="media-ai-caption">
                <Sparkle size={12} className="mr-1" /> {busy === "caption" ? "Writing…" : "AI caption from photo"}
              </Button>
            </div>
            {(asset.quality_warnings || []).map((w) => (
              <div key={w} className="text-[11px] text-amber-300 mt-2 flex items-center gap-1"><Warning size={12} /> {w}</div>
            ))}
          </div>
          <div className="space-y-2.5 text-sm">
            {[["title", "Name"], ["description", "Description"], ["alt_text", "Alt text"],
              ["project", "Project"], ["location", "Location"], ["customer_ref", "Customer / job ref"]].map(([k, l]) => (
              <div key={k}>
                <Label className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">{l}</Label>
                {k === "description"
                  ? <Textarea rows={2} value={m[k] || ""} onChange={(e) => setM({ ...m, [k]: e.target.value })} className="mt-1" data-testid={`media-edit-${k}`} />
                  : <Input value={m[k] || ""} onChange={(e) => setM({ ...m, [k]: e.target.value })} className="mt-1 h-8" data-testid={`media-edit-${k}`} />}
              </div>
            ))}
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">Tags (comma-separated)</Label>
              <Input value={Array.isArray(m.tags) ? m.tags.join(", ") : m.tags || ""} onChange={(e) => setM({ ...m, tags: e.target.value })}
                className="mt-1 h-8" data-testid="media-edit-tags" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">Category</Label>
              <Select value={m.category || "Other"} onValueChange={(v) => setM({ ...m, category: v })}>
                <SelectTrigger className="mt-1 h-8 bg-white/5 border-white/10" data-testid="media-edit-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#16161c] border-white/10 text-white max-h-64">
                  {(usageQ.data ? null : null) || ["Before Photos", "After Photos", "Completed Projects", "Work in Progress", "Products", "Team Members", "Equipment", "Customer Testimonials", "Events", "Buildings", "Properties", "Food", "Vehicles", "Promotions", "Logos", "Brand Graphics", "Seasonal Content", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={save} disabled={busy === "save"} className="w-full bg-white/10 hover:bg-white/15" data-testid="media-edit-save">Save details</Button>
            <div className="rounded-lg border border-white/10 p-3">
              <div className="text-[11px] uppercase tracking-wider text-[#9a9a9a] mb-1.5">Used in {(usageQ.data?.usage || []).length} post(s)</div>
              {(usageQ.data?.usage || []).slice(0, 5).map((u, i) => (
                <div key={i} className="text-xs text-[#c9c9c9] truncate">• {u.title} <span className="text-[#9a9a9a]">({u.platform}, {(u.used_at || "").slice(0, 10)})</span></div>
              ))}
              {!(usageQ.data?.usage || []).length && <div className="text-xs text-[#9a9a9a]">Not used yet.</div>}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PrefsCard({ cid }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["media-prefs", cid], queryFn: () => mediaPrefs(cid), enabled: !!cid });
  const p = q.data || {};
  const set = async (k, v) => {
    try {
      await mediaSetPrefs({ client_id: cid, [k]: v });
      qc.invalidateQueries({ queryKey: ["media-prefs", cid] });
      toast.success("Media preference saved");
    } catch (e) { toast.error("Could not save preference"); }
  };
  const rows = [
    ["prefer_uploaded", "Prefer my uploaded photos", "Auto Pilot picks your real photos before generating AI images"],
    ["allow_ai", "Allow AI images when needed", "Used only when no suitable real photo exists"],
    ["never_ai", "Never use AI images", "Posts without a matching real photo will wait for your attention"],
  ];
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3" data-testid="media-prefs-card">
      <div className="font-semibold text-sm">Auto Pilot media preferences</div>
      {rows.map(([k, l, d]) => (
        <div key={k} className="flex items-center justify-between gap-3">
          <div><div className="text-sm">{l}</div><div className="text-[11px] text-[#9a9a9a]">{d}</div></div>
          <Switch checked={!!p[k]} onCheckedChange={(v) => set(k, v)} data-testid={`pref-${k}`} />
        </div>
      ))}
      <div className="flex items-center justify-between gap-3">
        <div><div className="text-sm">Don't reuse a photo within</div><div className="text-[11px] text-[#9a9a9a]">Keeps your feed varied</div></div>
        <div className="flex items-center gap-1.5">
          <Input type="number" className="h-8 w-16" value={p.no_reuse_days ?? 14}
            onChange={(e) => set("no_reuse_days", Number(e.target.value))} data-testid="pref-no-reuse-days" />
          <span className="text-xs text-[#9a9a9a]">days</span>
        </div>
      </div>
    </div>
  );
}

export default function MediaLibraryPage() {
  const { activeId, activeClient } = useClient();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("newest");
  const [showArchived, setShowArchived] = useState(false);
  const [edit, setEdit] = useState(null);
  const listQ = useQuery({
    queryKey: ["media", activeId, q, category, sort, showArchived],
    queryFn: () => mediaList(activeId, { q, category: category === "all" ? "" : category, sort, archived: showArchived }),
    enabled: !!activeId,
  });
  const d = listQ.data || {};
  const assets = d.assets || [];
  const inv = () => qc.invalidateQueries({ queryKey: ["media", activeId] });

  const quick = async (id, patch, msg) => {
    try { await mediaPatch(id, patch); toast.success(msg); inv(); }
    catch { toast.error("Action failed"); }
  };

  return (
    <PageContainer>
      <PageHeader eyebrow="Your Content" title="Media Library"
        subtitle={`Real photos of ${activeClient?.name || "your business"} — used across posts, campaigns and ads. AI images stay optional.`} />
      <div className="space-y-5">
        <UploadZone cid={activeId} onDone={inv} />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Input placeholder="Search photos, projects, tags…" value={q} onChange={(e) => setQ(e.target.value)}
                className="h-9 w-64" data-testid="media-search" />
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-9 w-48 bg-white/5 border-white/10" data-testid="media-category-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#16161c] border-white/10 text-white max-h-64">
                  <SelectItem value="all">All categories</SelectItem>
                  {(d.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="h-9 w-36 bg-white/5 border-white/10" data-testid="media-sort"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#16161c] border-white/10 text-white">
                  {[["newest", "Newest"], ["oldest", "Oldest"], ["category", "Category"], ["project", "Project"], ["usage", "Most used"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <label className="flex items-center gap-1.5 text-xs text-[#9a9a9a] cursor-pointer ml-auto">
                <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-[#FF5E00]" data-testid="media-archived-toggle" />
                Archived
              </label>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="media-grid">
              {assets.map((a) => (
                <div key={a.id} className="group rounded-xl overflow-hidden border border-white/10 bg-white/[0.02] hover:border-white/25 transition-colors" data-testid={`media-card-${a.id}`}>
                  <button className="w-full" onClick={() => setEdit(a)}>
                    <img src={`${BACKEND}${a.thumb_path || a.path}`} alt={a.alt_text || a.title} className="w-full h-32 object-cover" loading="lazy" />
                  </button>
                  <div className="p-2.5">
                    <div className="text-xs font-medium truncate">{a.title}</div>
                    <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                      <Badge className={`border-0 text-[9px] px-1.5 ${SOURCE_STYLE[a.source] || "bg-white/10"}`}>{(a.source || "").replace(/_/g, " ")}</Badge>
                      {a.category && <span className="text-[9px] text-[#9a9a9a]">{a.category}</span>}
                      {(a.usage_count || 0) > 0 && <span className="text-[9px] text-[#9a9a9a]">· used {a.usage_count}×</span>}
                    </div>
                    <div className="flex items-center gap-1 mt-2 opacity-70 group-hover:opacity-100">
                      <button title="Favorite" data-testid={`media-fav-${a.id}`} onClick={() => quick(a.id, { favorite: !a.favorite }, a.favorite ? "Removed from favorites" : "Marked favorite")}>
                        <Star size={14} weight={a.favorite ? "fill" : "regular"} className={a.favorite ? "text-amber-400" : "text-[#9a9a9a]"} />
                      </button>
                      <button title={a.archived ? "Unarchive" : "Archive"} data-testid={`media-archive-${a.id}`} onClick={() => quick(a.id, { archived: !a.archived }, a.archived ? "Restored" : "Archived")}>
                        <Archive size={14} className="text-[#9a9a9a]" />
                      </button>
                      <button title="Delete" className="ml-auto" data-testid={`media-delete-${a.id}`}
                        onClick={async () => { if (window.confirm("Delete this photo permanently?")) { await mediaDelete(a.id); toast.success("Deleted"); inv(); } }}>
                        <Trash size={14} className="text-red-400/80" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {!assets.length && !listQ.isLoading && (
                <div className="col-span-full rounded-xl border border-dashed border-white/15 p-8 text-center text-sm text-[#9a9a9a]">
                  <Images size={26} className="mx-auto mb-2 text-[#9a9a9a]" />
                  No photos here yet. Upload real photos of your work, products, team and projects — Vozelto will use them before any AI image.
                </div>
              )}
            </div>
          </div>
          <div className="space-y-4">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="media-storage-card">
              <div className="flex items-center gap-2 text-sm font-semibold"><HardDrives size={16} className="text-[#FF5E00]" /> Storage</div>
              <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-[#FF5E00]" style={{ width: `${d.storage_limit_mb > 0 ? Math.min(100, (d.storage_used_mb / d.storage_limit_mb) * 100) : 5}%` }} />
              </div>
              <div className="text-xs text-[#9a9a9a] mt-1.5" data-testid="media-storage-text">
                {d.storage_used_mb ?? 0} MB used {d.storage_limit_mb > 0 ? `of ${d.storage_limit_mb} MB on your plan` : "· unlimited on your plan"}
              </div>
            </div>
            <PrefsCard cid={activeId} />
          </div>
        </div>
      </div>
      {edit && <EditDialog asset={edit} cid={activeId} onClose={() => setEdit(null)} />}
    </PageContainer>
  );
}
